import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  BackButton,
  CancelButton,
  SubmitButton,
} from "../../../../../Components/Utils/Buttons";
import {
  NuInput,
  NuTextArea,
} from "../../../../../Components/Utils/Input";
import FormikSelectTwo from "../../../../../Components/Utils/Select";
import DropDownAddMetaData from "../../../../../Components/Utils/selectAddMetaData";
import Breadcrumb from "../../../../../Components/Utils/Breadcrumb";
import UseEditData from "../../../../../Components/Hooks/useEditData";
import UseDropDown from "../../../../../Components/Hooks/useDropDown";
import useFetchMataData from "../../../../../Components/Hooks/useFetchMataData";
import axiosInstance from "../../../../../Services/axiosCommon";

export default function EditParticipantPage() {
  const { participantId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const from = location.state?.from;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const { Oncancel, editData, contextHolder, isLoading } = UseEditData();
  const { data: usersList } = UseDropDown("/user/drop-down");
  const { value: roleOptions, getData: refRoleOptions } = useFetchMataData(
    `/dropdown/dropdown-details/`,
    "MeetingParticipant_Role"
  );
  const { value: statusOptions, getData: refStatusOptions } = useFetchMataData(
    `/dropdown/dropdown-details/`,
    "MeetingParticipant_Status"
  );

  const [participantData, setParticipantData] = useState(stateData || null);
  const [selectedUserDetails, setSelectedUserDetails] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    department: "",
  });

  useEffect(() => {
    if (!stateData && participantId) {
      axiosInstance
        .get(`/meetings/participants/${participantId}`)
        .then((res) => {
          if (res?.data?.data) {
            setParticipantData(res.data.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching participant details:", err);
        });
    }
  }, [stateData, participantId]);

  const initialValues = useMemo(() => {
    return {
      user_id: participantData?.user_id || participantData?.userId || "",
      meeting_role:
        participantData?.meeting_role || participantData?.role || "Attendee",
      status: participantData?.status || "Invited",
      special_notes:
        participantData?.special_notes || participantData?.notes || "",
    };
  }, [participantData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: yup.object({
      user_id: yup.string().required("User is required!"),
      meeting_role: yup.string().required("Meeting role is required!"),
      status: yup.string().required("Status is required!"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        user_id: Number(values.user_id),
        meeting_role: values.meeting_role,
        status: values.status,
        special_notes: values.special_notes || "",
      };

      const targetId = participantId || participantData?.id;
      editData(
        `/meetings/participants/${targetId}`,
        payload,
        resetForm,
        from,
        { currentPage: pageIndex, searchQuery: searchQuery }
      );
    },
  });

  useEffect(() => {
    if (formik.values.user_id) {
      const matchedUser = (usersList || []).find(
        (u) => String(u.value || u.id) === String(formik.values.user_id)
      );

      axiosInstance
        .get(`/user/user-details/${formik.values.user_id}`)
        .then((res) => {
          const u = res.data?.data || res.data;
          if (u) {
            const phoneStr =
              typeof u.phone === "object" && u.phone !== null
                ? u.phone.display || u.phone.number || ""
                : u.phone || u.mobile || "";

            setSelectedUserDetails({
              name: u.name || matchedUser?.label || "",
              email: u.email || "",
              phone: phoneStr,
              designation: u.designation || u.position || "",
              department: u.division || u.department || "",
            });
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user details:", err);
          if (matchedUser) {
            setSelectedUserDetails({
              name: matchedUser.label || "",
              email: matchedUser.email || "",
              phone: matchedUser.phone || "",
              designation: matchedUser.designation || "",
              department: matchedUser.department || matchedUser.division || "",
            });
          }
        });
    }
  }, [formik.values.user_id, usersList]);

  return (
    <div className="w-full h-full p-2 lg:px-4">
      <div className="flex justify-between items-center mb-4">
        <Breadcrumb
          items={[
            { name: "Meeting" },
            {
              name: "Participants",
              link: "/meeting/participants",
            },
            {
              name: "Edit Participant",
            },
          ]}
        />
        <Link
          to={from || "/meeting/participants"}
          state={{
            currentPage: pageIndex,
            searchQuery: searchQuery || "",
          }}
        >
          <BackButton link={true} />
        </Link>
      </div>

      <div className="w-full h-[calc(100vh-140px)] overflow-y-auto">
        <form onSubmit={formik.handleSubmit}>
          <div className="w-full mx-auto px-2 pb-4 bg-white rounded-md">
            {/* Section 1: User Information */}
            <div className="w-full">
              <p className="py-2 px-4 text-xs 2xl:text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                User Information :
              </p>
            </div>

            <div className="w-full flex justify-start content-start flex-wrap">
              <FormikSelectTwo
                label="Select User"
                placeholder="Choose User"
                Options={usersList || []}
                formik={formik}
                name="user_id"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="text"
                label="Email"
                placeholder="Auto-filled from user profile"
                value={selectedUserDetails.email}
                name="email_display"
                width="w-full md:w-1/2 lg:w-1/3"
                disabled={true}
              />

              <NuInput
                type="text"
                label="Phone Number"
                placeholder="Auto-filled from user profile"
                value={selectedUserDetails.phone}
                name="phone_display"
                width="w-full md:w-1/2 lg:w-1/3"
                disabled={true}
              />

              <NuInput
                type="text"
                label="Designation"
                placeholder="Auto-filled from user profile"
                value={selectedUserDetails.designation}
                name="designation_display"
                width="w-full md:w-1/2 lg:w-1/3"
                disabled={true}
              />

              <NuInput
                type="text"
                label="Department"
                placeholder="Auto-filled from user profile"
                value={selectedUserDetails.department}
                name="department_display"
                width="w-full md:w-1/2 lg:w-1/3"
                disabled={true}
              />
            </div>

            {/* Section 2: Participation Details */}
            <div className="w-full mt-4">
              <p className="py-2 px-4 text-xs 2xl:text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                Participation Details :
              </p>
            </div>
            <div className="w-full flex justify-start content-start flex-wrap">
              <DropDownAddMetaData
                label="Meeting Role"
                placeholder="Select Meeting Role"
                Options={roleOptions || []}
                formik={formik}
                name="meeting_role"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
                getFunc={refRoleOptions}
                displayProperty="Meeting Role"
                propertyName="MeetingParticipant_Role"
              />

              <DropDownAddMetaData
                label="Status"
                placeholder="Select Status"
                Options={statusOptions || []}
                formik={formik}
                name="status"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
                getFunc={refStatusOptions}
                displayProperty="Status"
                propertyName="MeetingParticipant_Status"
              />

              <NuTextArea
                label="Special Notes"
                placeholder="Enter special notes"
                formik={formik}
                name="special_notes"
                width="w-full md:w-1/2 lg:w-2/3"
              />
            </div>

            <div className="w-full flex justify-end gap-4 p-4 mt-6">
              <CancelButton
                handleClick={() => {
                  Oncancel(from, {
                    currentPage: pageIndex,
                    searchQuery: searchQuery,
                  });
                }}
              />
              <SubmitButton isLoading={isLoading} />
            </div>
          </div>
        </form>
      </div>
      {contextHolder}
    </div>
  );
}